import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Gespeicherten Korb gleich beim ersten Rendern lesen statt in einem Effekt: Effekte der Kinder laufen
  // vor denen des Providers, ein Laden per Effekt hätte das Leeren nach dem Kauf (KaufHinweis) sofort
  // wieder überschrieben.
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('davidmedia_cart');
      const daten = savedCart ? JSON.parse(savedCart) : [];
      return Array.isArray(daten) ? daten : [];
    } catch (e) {
      console.error('Error loading cart from localStorage:', e);
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('davidmedia_cart', JSON.stringify(cartItems));
    } catch (e) { /* ohne Speicher lebt der Korb nur bis zum Neuladen */ }
  }, [cartItems]);

  // Add item to cart
  const addToCart = (product, licenseType = 'single', quantity = 1, appliedDiscountCode = null) => {
    setCartItems(prev => {
      const existingIndex = prev.findIndex(
        item => item.productId === product.id && item.licenseType === licenseType
      );

      if (existingIndex >= 0) {
        // Update quantity if item already exists
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }

      // Add new item
      return [...prev, {
        productId: product.id,
        name: product.name,
        price: product.priceValue,
        licenseType,
        quantity,
        image: typeof product.images[0] === 'object' ? product.images[0].url : product.images[0]
      }];
    });
    
    // If a discount code was applied on the product page, set it in cart
    if (appliedDiscountCode && !discountCode) {
      setDiscountCode(appliedDiscountCode);
    }
  };

  // Remove item from cart
  const removeFromCart = (productId, licenseType) => {
    setCartItems(prev => prev.filter(
      item => !(item.productId === productId && item.licenseType === licenseType)
    ));
  };

  // Update item quantity
  const updateQuantity = (productId, licenseType, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, licenseType);
      return;
    }

    setCartItems(prev => prev.map(item => {
      if (item.productId === productId && item.licenseType === licenseType) {
        return { ...item, quantity };
      }
      return item;
    }));
  };

  // Update license type for an item
  const updateLicenseType = (productId, oldLicenseType, newLicenseType) => {
    setCartItems(prev => {
      // Check if there's already an item with the new license type
      const existingIndex = prev.findIndex(
        item => item.productId === productId && item.licenseType === newLicenseType
      );

      if (existingIndex >= 0) {
        // Merge quantities
        const oldItem = prev.find(
          item => item.productId === productId && item.licenseType === oldLicenseType
        );
        
        return prev
          .map(item => {
            if (item.productId === productId && item.licenseType === newLicenseType) {
              return { ...item, quantity: item.quantity + (oldItem?.quantity || 0) };
            }
            return item;
          })
          .filter(item => !(item.productId === productId && item.licenseType === oldLicenseType));
      }

      // Just update the license type
      return prev.map(item => {
        if (item.productId === productId && item.licenseType === oldLicenseType) {
          return { ...item, licenseType: newLicenseType };
        }
        return item;
      });
    });
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
    setDiscountCode('');
    setDiscountInfo(null);
  };

  // Calculate item price based on license type
  const getItemPrice = (item) => {
    if (item.licenseType === 'team') {
      // Team license: 5x price with 15% discount
      return item.price * 5 * 0.85;
    }
    return item.price;
  };

  // Calculate subtotal
  const getSubtotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (getItemPrice(item) * item.quantity);
    }, 0);
  };

  // Calculate discount amount
  const getDiscountAmount = () => {
    if (!discountInfo) return 0;

    const subtotal = getSubtotal();
    
    if (discountInfo.scope === 'cart_total' || discountInfo.scope === 'all_products') {
      return subtotal * (discountInfo.discount_percent / 100);
    }
    
    if (discountInfo.scope === 'specific_product' && discountInfo.product_id) {
      const productTotal = cartItems
        .filter(item => item.productId === discountInfo.product_id)
        .reduce((total, item) => total + (getItemPrice(item) * item.quantity), 0);
      return productTotal * (discountInfo.discount_percent / 100);
    }

    return 0;
  };

  // Calculate total
  const getTotal = () => {
    return getSubtotal() - getDiscountAmount();
  };

  // Get total items count
  const getItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  // Open/close cart
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen(prev => !prev);

  const value = {
    cartItems,
    isCartOpen,
    discountCode,
    discountInfo,
    setDiscountCode,
    setDiscountInfo,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateLicenseType,
    clearCart,
    getItemPrice,
    getSubtotal,
    getDiscountAmount,
    getTotal,
    getItemCount,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
